import csiCard, { CardSpecs } from "csi/card/_base";
import { ILocaleProvider } from "designSystem/locale";
import { QRY_LIMIT_PATH } from "csi/constants";
import ExtendedPath from "csi/core/ExtendedPath";
import DataStoreSelectionStore from "csi/datastore/DataStoreSelectionStore";
import NodeDataStore from "csi/datastore/NodeDataStore";
import Spec from "csi/core/Spec";
import { getRuntimeRulesFromNode, getRuntimeRulesQuery, RuleSpec, RULE_FILTER_ALL_ITEMS, RuntimeRulesSchema } from "csi/middleware/attributeRule/RuleService";
import AttributeSettingStore from "csi/middleware/AttributeSettingStore";
import { CnlsAndValues, removeDuplicatedSpecs } from "csi/middleware/common";
import FilterStore from "csi/middleware/filter/FilterStore";
import SearchManager from "csi/page/SearchManager";
import { getSearch } from "csi/service/dataStoreService";
import { resolveNode } from "csi/service/nodeService";
import { submitWriter } from "csi/service/writerService";
import { maxResultSize } from "csi/sessionConstants";
import { ReactionStore } from "csi/utility/mobxUtility";
import { RootStore } from "csi/state/rootStore";
import WorkingCache from "csi/state/WorkingCache";
import { C8Node, cnl } from "csi/typings";
import { pathCreate, PATH_OP_PARENT } from "csi/utility/pathUtility";
import xml from "csi/xml";
import Query from "csi/xml/Query";
import Search from "csi/xml/Search";
import Writer from "csi/xml/Writer";
import _ from "lodash";
import { action, computed, makeObservable, observable, ObservableMap, reaction, runInAction } from "mobx";
import { createContext, useContext } from "react";
import { PPWizardStore } from "./NewProductPresentation/PPWizardStore";
import ProductGalleryStore from "./NewProductPresentation/ProductGalleryStore";
import {
	BASEPRODUCT_PRESENTATION_SET_ITEMS,
	excludeSpecialStyles,
	EXCLUDE_ITEM_IDS,
	getAdHocSpecs,
	getAttributeRuleSpecsForType,
	getColorMatrixPath,
	getPresentationPrimaryType,
	getProductDependencyPaths,
	isPresentationEditor,
	onApplyPresentationProductFilter,
	PRESENTATION_PRODUCTS,
	PRESENTATION_RULE,
	processProductViolations,
	QUICK_FILTERS,
	setAdHocIds,
	STYLE
} from "./PPService";
import PresentationPageStore from "./PresentationPageStore";
import PresentationSetsStore from "./store/PresentationSetsStore";
import SetEditStore from "./store/SetEditStore";

const {
	ItemsAll,
	ItemsUsedInSets,
	ItemsNotUsedInSets,
	ItemsWithViolations,
	ItemsWithoutViolations,
} = QUICK_FILTERS;

class PPProductLayoutStore extends ReactionStore {
	@observable.ref private _dataStore: NodeDataStore;
	@observable.ref private _fixedEditSetStore: SetEditStore;
	@observable.ref private _setsStore: PresentationSetsStore;
	@observable.ref private _filterSpecs: Spec[];
	@observable.ref private _filterStore: FilterStore;
	private readonly _presentationUrl: cnl;
	private readonly _rootStore: RootStore;
	@observable private _ruleDialogStore: PPWizardStore;
	@observable private _searchText: string;
	@observable private _specs: Spec[];


	/** node cache */
	readonly cache: WorkingCache;

	/** locale provider */
	readonly locale: ILocaleProvider;

	/** store for managing selections if in selection dialog */
	readonly selectionStore: DataStoreSelectionStore;

	/** adHoc specs from local storage */
	@computed get adHocSpecs(): Spec[] {
		const { primaryType, secondaryType } = this;
		return getAdHocSpecs(primaryType, secondaryType);
	}

	/** item ids already selected  */
	@computed get preSelectIds(): string[] {
		const { specs, filterSpecs } = this;

		return [...(specs || []), ...(filterSpecs || [])].map((spec) => spec.key.toString());
	}

	/** attribute setting store */
	@computed get attributeSettingStore(): AttributeSettingStore | null {
		const { primaryType, secondaryType, preSelectIds, specs, filterStore } = this;
		const { setSpecs } = filterStore || {};

		const setAdHocAndUpdate = (primaryIds: string[], secondaryIds: string[]): void => {
			runInAction(() => {
				setAdHocIds([primaryType, primaryIds], [secondaryType, secondaryIds]);
				const { adHocSpecs } = this;

				const finalSpecs = removeDuplicatedSpecs([...specs, ...adHocSpecs]);
				setSpecs?.(finalSpecs);
			});
		};

		return preSelectIds ? new AttributeSettingStore(preSelectIds, [primaryType, secondaryType], setAdHocAndUpdate, EXCLUDE_ITEM_IDS) : null;
	}

	/** base search for products */
	@computed get baseSearch(): Search | undefined {
		const { _presentationUrl, primaryType } = this;

		return getSearch("BaseProduct.ProductPresentation", _presentationUrl, { type: primaryType });
	}

	/** cnl list to get the nodes with violations and without violations  */
	@computed get cnlListForViolations(): Set<cnl> | null {
		const { filterStore, violationUrls, presentation } = this;
		const { quickFilterId } = filterStore || {};

		if (quickFilterId === ItemsWithViolations) {
			return violationUrls;
		} else if (quickFilterId === ItemsWithoutViolations) {
			return new Set(_.difference(presentation.PPProducts, Array.from(violationUrls)));
		}

		return null;
	}

	/** indicate if show the available actions */
	@computed get showActions(): boolean {
		return !this.selectionStore && isPresentationEditor();
	}

	/** matrix path from product type to its colors */
	@computed get colorMatrixPath(): ExtendedPath | undefined {
		return getColorMatrixPath(this.primaryType);
	}

	/** query for colors according to the rules */
	@computed get colorQuery(): Query {
		const { colorMatrixPath, colorRuleQuery, filterStore } = this;
		const query = new Query();

		if (colorRuleQuery) {
			query.importQuery(colorRuleQuery);
		}

		filterStore?.applyFiltersToQuery(query, colorMatrixPath);
		return query;
	}

	/** query for colors according to the rules */
	@computed get colorRuleQuery(): Query | null {
		const { colorMatrixPath, runtimeRules } = this;
		return getRuntimeRulesQuery(runtimeRules, colorMatrixPath);
	}

	/** query for the data store based on search and filters */
	@computed get dataStoreQuery(): Query {
		const { filterStore, searchText, slimCardSpecs, filterQueryForQuickFilter } = this;
		const q = new Query();
		filterStore?.applyFiltersToQuery(q, null, onApplyPresentationProductFilter);

		// add search text to query
		if (searchText) {
			csiCard.getQueryForSpecs(slimCardSpecs, searchText, q);
		}

		// apply quick filter if any
		if (filterQueryForQuickFilter) {
			q.importQuery(filterQueryForQuickFilter);
		}

		return q;
	}

	/** store managing editing of a ProductPresentationSet from either product view or set view */
	get editSetStore(): SetEditStore {
		const { _fixedEditSetStore, _setsStore } = this;
		return _fixedEditSetStore || _setsStore?.editSetStore;
	}

	/** the specs which are being used to filter by */
	get filterSpecs(): Spec[] {
		return this._filterSpecs;
	}

	/** the store for the filter */
	get filterStore(): FilterStore {
		return this._filterStore;
	}

	/** query to filter out products already in presentation, and those that don't match rules */
	@computed get filterQueryForMoreProducts(): Query {
		const { productRuleQuery, _presentationUrl, primaryType } = this;
		const q = new Query();
		q.importQuery(productRuleQuery);
		q.Not().Node(xml.PROP_URL, xml.OP_EQUALS, _presentationUrl, pathCreate(PATH_OP_PARENT, PRESENTATION_PRODUCTS));

		// filter out the template in the case of Style
		if (STYLE === primaryType) {
			excludeSpecialStyles(q);
		}

		return q;
	}

	/** query for quick filter */
	@computed get filterQueryForQuickFilter(): Query | null {
		const { _presentationUrl, filterStore } = this;
		const { quickFilterId } = filterStore || {};

		if (quickFilterId === ItemsUsedInSets) {
			return new Query().RefAttr(xml.ATTR_PARENT, xml.OP_EQUALS, _presentationUrl, pathCreate(BASEPRODUCT_PRESENTATION_SET_ITEMS, xml.ATTR_PARENT));
		} else if (quickFilterId === ItemsNotUsedInSets) {
			const currentQuery = new Query();
			currentQuery.Not().RefAttr(xml.ATTR_PARENT, xml.OP_EQUALS, _presentationUrl, pathCreate(BASEPRODUCT_PRESENTATION_SET_ITEMS, xml.ATTR_PARENT));

			return currentQuery;
		}

		return null;
	}

	/** store managing editing of a fixed ProductPresentationSet (edit a specific set) */
	get fixedEditSetStore(): SetEditStore {
		return this._fixedEditSetStore;
	}

	/** store used to query products matching rules */
	@computed get galleryStore(): ProductGalleryStore {
		const {
			onAddToSet,
			onRemoveProduct,
			selectionStore,
			showAttributes,
			showActions,
			store,
			_presentationUrl,
		} = this;

		return new ProductGalleryStore(
			store,
			showAttributes,
			null,
			_presentationUrl,
			selectionStore ? null : onAddToSet,
			showActions ? onRemoveProduct : null,
		);
	}

	/** indicates whether the initial state of the top drawer is open or not */
	get initialOpen(): boolean {
		return this.ruleSpecs?.length > 0;
	}

	/** indicates whether the sets gallery is displaying to the right */
	get isDisplayingSets(): boolean {
		return !!this._setsStore && !this.editSetStore;
	}

	/** paths relative to product needed to display cards */
	@computed get productDependencyPaths(): string[] {
		const { ruleSpecs, showAttributes } = this;
		const specs = ruleSpecs?.map((ruleSpec) => ruleSpec.spec);
		return getProductDependencyPaths(showAttributes, specs, true);
	}

	/** ProductPresentation node */
	get presentation(): C8Node {
		return this.cache.get(this._presentationUrl);
	}

	/** primary node type in PP */
	get primaryType(): string {
		return getPresentationPrimaryType(this.presentation);
	}

	/** query for products according to the rules */
	@computed get productRuleQuery(): Query | null {
		return getRuntimeRulesQuery(this.runtimeRules, null, onApplyPresentationProductFilter);
	}

	/** resources for quick filters */
	@computed get quickFilterResources(): string[] {
		return [
			ItemsAll,
			ItemsUsedInSets,
			ItemsNotUsedInSets,
			ItemsWithViolations,
			ItemsWithoutViolations,
		];
	}

	/** number of products in the store */
	get resultCount(): number {
		return this.store?.totalCount || 0;
	}

	/** limit of the number of styles that can be added or displayed */
	get resultCountLimit(): number {
		return maxResultSize;
	}

	/** indicates if adding styles is disallowed because count is over limit */
	get resultCountOverLimit(): boolean {
		return this.resultCount > this.resultCountLimit;
	}

	/** runtime rules  */
	get ruleSpecs(): RuleSpec[] {
		return this.runtimeRules?.ruleSpecs || [];
	}

	/** store for edit rule dialog */
	@computed get ruleDialogStore(): PPWizardStore {
		return this._ruleDialogStore;
	}

	/** rules for presentation */
	@computed get runtimeRules(): RuntimeRulesSchema | null {
		const { cache, colorMatrixPath, presentation } = this;
		const ruleNode = cache.get(presentation[PRESENTATION_RULE]);
		return ruleNode ? getRuntimeRulesFromNode(ruleNode, colorMatrixPath) : null;
	}

	/** search text for slim card */
	get searchText(): string {
		return this._searchText;
	}

	/** return secondary type for given type of product to its colors (if any) */
	@computed get secondaryType(): string {
		const { primaryType } = this;
		const colorMatrixPath = getColorMatrixPath(primaryType);

		return colorMatrixPath?.getEndType() || "";
	}

	/** the store for the sets in the right pane */
	get setsStore(): PresentationSetsStore {
		return this._setsStore;
	}

	/** indicate if attributes should be shown for each product */
	get showAttributes(): boolean {
		const { localStore: { productPresentation: { showAttributes } } } = this._rootStore;
		return showAttributes;
	}

	/** slim card specs for the product */
	@computed get slimCardSpecs(): CardSpecs {
		const { primaryType } = this;
		return csiCard.getAttributeCardSlimSpecs(primaryType);
	}

	/** specs admin configures */
	get specs(): Spec[] {
		return this._specs;
	}

	/** generate data store for the products */
	get store(): NodeDataStore {
		return this._dataStore;
	}

	/** the url list of products with violation(s), including the ones due to both products and colors  */
	get violationUrls(): Set<cnl> | undefined {
		const { violationUrls } = this._rootStore.pageStore as PresentationPageStore || {};

		return violationUrls;
	}

	/** add given product/color combination to a set; if none selected, open right pane to select one */
	@action onAddToSet = (productUrl: cnl, colorUrl?: cnl, skipAddingItem?: boolean): void => {
		const { cache, editSetStore, _presentationUrl, _rootStore: { rightPaneStore }, filterStore } = this;

		if (editSetStore && !skipAddingItem) {
			editSetStore.addItem(productUrl, colorUrl);
		} else {
			const onSaveSet = (): void => {
				// need to rerun the query to reflect the changes if quick filter is applied
				if (filterStore.quickFilterId != RULE_FILTER_ALL_ITEMS) {
					this.createDataStore();
				}
			};

			// create store to display available sets if not already created
			if (!this._setsStore) {
				this._setsStore = new PresentationSetsStore(cache, _presentationUrl, action(() => {
					this._setsStore = null;
				}), onSaveSet);

				rightPaneStore.setAdHocComponent(this._setsStore);
			}

			this._setsStore.setProductAndColor(productUrl, colorUrl);
		}
	};

	/** post operation to remove product from presentation */
	onRemoveProduct = async (productUrl: cnl): Promise<void> => {
		const { _dataStore, _presentationUrl, filterStore, cache } = this;
		const writer = new Writer();

		writer.callMethod("ProductPresentation", "RemoveProductFromPresentation")
			.addParameter("PresentationUrl", xml.ATTR_CNL, _presentationUrl)
			.addParameter("ProductUrl", xml.ATTR_CNL, productUrl);

		const response = await submitWriter(writer);
		cache.cacheResponse(response);

		_dataStore.remove(productUrl);

		// apply the change so that the left filter pane refresh
		filterStore.reloadValuesStore();

		// apply the change to the pill store as well
		filterStore.deleteProductFromFilterValuesMap(productUrl);
	};

	/** add new products to the presentation */
	@action saveProducts = async (productUrls: cnl[]): Promise<void> => {
		const writer = new Writer();
		writer.changeNode(this._presentationUrl).changeAttribute(PRESENTATION_PRODUCTS, xml.ATTR_REFLIST, productUrls, true);
		await submitWriter(writer);
		this.createDataStore();

		// update the counts in the left panel
		this.filterStore.reloadValuesStore();

		// add the cnls of added products to the map if matches the existing filters
		this.filterStore.addProductCnlIfMatchesTheFilter(productUrls.map((url) => this.cache.get(url)), this.cache);
	};

	/** select all results if adding products */
	@action selectAll = (): void => {
		const { selectionStore, _dataStore: { urls } } = this;

		if (selectionStore) {
			for (const id of urls) {
				selectionStore.addSelection(id);
			}
		}
	};

	/** set edit store for a fixed presentation set */
	@action setFixedEditSetStore = (editSetStore: SetEditStore): void => {
		this._fixedEditSetStore = editSetStore;
	};

	/** set search text to search for slim card */
	@action setSearchText = (searchText: string): void => {
		this._searchText = searchText;
	};

	/** initialize the local filter store */
	private _initialize = async (): Promise<void> => {
		const {
			adHocSpecs,
			cache,
			filterQueryForMoreProducts,
			presentation,
			primaryType,
			selectionStore,
			quickFilterResources,
		} = this;

		// request node holding rules for presentation
		await resolveNode(presentation[PRESENTATION_RULE], cache);

		// get rule specs for selected product type
		const specs = await getAttributeRuleSpecsForType(primaryType);

		// callback to update the specs 
		const postFilter = action((filterValuesMap: ObservableMap<Spec, CnlsAndValues>) => {
			const specs = [];

			for (const [spec, { values }] of filterValuesMap) {
				specs.push(spec);
			}

			this._filterSpecs = specs;
		});

		const postQuickFilter = (quickFilterId: string): void => {
			// TO DO: need to find a way to only apply the rules to the specific node set(quick filters of violations and without violations)
			this.filterStore.setFilterQuery(this.filterQueryForQuickFilter);
		};

		runInAction(() => {
			this._specs = specs;

			const finalSpecs = removeDuplicatedSpecs([...specs, ...adHocSpecs]);
			this._filterStore = new FilterStore(
				primaryType,
				finalSpecs,
				selectionStore ? filterQueryForMoreProducts : null,
				selectionStore ? null : { [QRY_LIMIT_PATH]: xml.getUrlPath(presentation.$URL, pathCreate(PRESENTATION_PRODUCTS)) },
				postFilter,
				quickFilterResources,
				postQuickFilter,
			);

			this.createDataStore();
		});
	};

	/** initialize the edit rule store */
	@action initialRuleDialogStore = (): void => {
		const { specs, runtimeRules, _presentationUrl, cache, presentation, createDataStore, locale } = this;

		const postUpdateRule = async (): Promise<void> => {
			await processProductViolations(presentation, cache);

			// TODO: ideally, update the corresponding stores would make the components re-render, but for the PPProductCard component, tried making it 
			// react to the store, but it only get re-render and resize when mouse over, maybe figure it out when still have time
			createDataStore();
		};

		const { createPresentationWizardStore } = this._rootStore.pageStore as PresentationPageStore;

		this._ruleDialogStore = createPresentationWizardStore(locale, null, null, null, _presentationUrl, runtimeRules, specs, cache, postUpdateRule);
	};

	/** create data store based on changes to filters */
	@action createDataStore = (): void => {
		const {
			cache,
			dataStoreQuery,
			filterQueryForMoreProducts,
			filterStore,
			cnlListForViolations,
			primaryType,
			productDependencyPaths,
			selectionStore,
			baseSearch,
			runtimeRules,
			violationUrls,
		} = this;

		const { quickFilterId } = filterStore;
		const violationWithoutRule = ((runtimeRules.ruleSpecs.length === 0 || _.isEmpty(violationUrls)) && quickFilterId === ItemsWithViolations);
		let search: Search;

		if (cnlListForViolations) {
			// if we already have cnls, then just fetch them from server, no need to apply to anything
			search = new Search();

			for (const cnl of cnlListForViolations) {
				search.setLimitUrl(cnl);
			}

			if (cnlListForViolations.size == 0) {
				// if no cnl, don't issue search at all
				search = null;
			} else {
				search.getQuery().importQuery(dataStoreQuery);
			}
		} else {
			search = baseSearch;

			if (selectionStore) {
				search = new Search().byType(primaryType);
				const query = search.getQuery();
				query.importQuery(filterQueryForMoreProducts);
			} else if (violationWithoutRule) {
				// if no rule at all and trying to filter out the items with violations, then return nothing
				search = null;
			}

			if (search) {
				search.getQuery().importQuery(dataStoreQuery);
			}
		}

		if (search) {
			SearchManager.getSearchTypeSortQuery(primaryType, false, search.getSortQuery());
			search.addDependencyPaths(productDependencyPaths);
		}

		this._dataStore = new NodeDataStore(search, cache);
	};

	/** focus on right pane if set has not yet been selected */
	_onDisplaySets = (): void => {
		this._rootStore.rightPaneStore.setFocusRight(this.isDisplayingSets);
	};

	constructor(rootStore: RootStore, node: C8Node, selectionStore?: DataStoreSelectionStore, locale?: ILocaleProvider) {
		super();
		makeObservable(this);

		this.selectionStore = selectionStore;
		this._rootStore = rootStore;
		this._presentationUrl = node.$URL;
		this.cache = rootStore.workingCache;
		this.cache.add(node);
		this.locale = locale;
		this._initialize();

		this.trackReaction(reaction(() => this.isDisplayingSets, this._onDisplaySets));

		// update data store when filters change, but throttle the function so does not spawn too many queries
		this.trackReaction(reaction(() => this.dataStoreQuery.toString(), _.throttle(this.createDataStore, 1000)));

		// need to react when limit node changes
		this.trackReaction(reaction(() => this.cnlListForViolations, _.throttle(this.createDataStore, 1000)));
	}
}

export default PPProductLayoutStore;

export const PPProductLayoutStoreContext = createContext<PPProductLayoutStore>(undefined);
export const usePPProductLayoutStore = (): PPProductLayoutStore => useContext(PPProductLayoutStoreContext);